/**
 * A fighter drawn for the menus from the generated art, as the fight draws it (docs/fight.md
 * §13): the body for the face, and both gloves at the rig's guard pose: the lead hand (the fist
 * image) in front, the rear hand its mirror, shaded. Fighters come from the asset manifest, so
 * new fighters work without code changes.
 */
import type { Look } from '@caged/shared';
import type { CSSProperties } from 'react';
import { colorCss, effectOf, gloveOf, gloveUrl, petOf } from '../../assets/look';
import { assetUrl, type AssetManifest, type BodyState, type FighterRig, type PetManifest } from '../../assets/manifest';
import { PET_LOOK } from '../../fight/render/config';
import { useManifest } from '../hooks';
import s from './FighterArt.module.css';

/** Body heights are drawn relative to this rig height, so taller fighters stay taller. */
const REFERENCE_DRAW_HEIGHT = 1.3;
/** Smaller fighters (cards, rows) go without their pet. */
const PET_MIN_HEIGHT = 90;
/** In the menus the pet flies beside the upper body (the fight flies it a little higher). */
const PET_MENU_HEIGHT = 0.62;

/**
 * A Locker pet beside a fighter drawn `h` px tall, as the fight places it (docs/fight.md §13):
 * behind the rear shoulder (left, as menu fighters face right), its wings flapping. At a KO it
 * sits on the floor in front of its fighter.
 */
function PetArt({ pet, rig, h, down }: { pet: PetManifest; rig: FighterRig; h: number; down: boolean }) {
  const S = h / rig.body.drawHeight;
  const ph = pet.height * S;
  const pw = ph * pet.bodyAspect;
  const x = down ? PET_LOOK.koX * S : -PET_LOOK.homeX * S;
  const style = {
    '--pw': `${pw}px`,
    '--ph': `${ph}px`,
    left: `calc(50% + ${x - pw / 2}px)`,
    bottom: down ? 0 : h * PET_MENU_HEIGHT,
  } as CSSProperties;
  const wing = pet.wing;
  const wings = [];
  if (wing && pet.wingImage) {
    const ww = wing.width * ph;
    const wh = ww * wing.aspect;
    const flap = wing.style === 'flap';
    const sides = flap ? [1, -1] : [1];
    for (const side of sides) {
      const rootX = pw / 2 + side * wing.at[0] * ph;
      const rootY = ph + wing.at[1] * ph;
      const wingStyle = {
        width: ww,
        height: wh,
        left: rootX - wing.root[0] * ww,
        top: rootY - wing.root[1] * wh,
        transformOrigin: `${wing.root[0] * 100}% ${wing.root[1] * 100}%`,
        animationDuration: `${(flap ? 0.5 : 0.25) / wing.hz}s`,
        '--up': `${-wing.swing}rad`,
        '--down': `${wing.swing}rad`,
      } as CSSProperties;
      const cls = down ? (side === 1 ? s.wingFoldR : s.wingFoldL) : !flap ? s.rotor : side === 1 ? s.wingR : s.wingL;
      wings.push(<img key={side} className={[s.petWing, cls].join(' ')} style={wingStyle} src={assetUrl(pet.wingImage)} alt="" draggable={false} />);
    }
  }
  const glow = pet.glow && !down && (
    <span
      className={s.petGlow}
      style={{ '--gc': pet.glow.color, width: pet.glow.size * ph, height: pet.glow.size * ph, left: pw / 2 + pet.glow.at[0] * ph, top: ph + pet.glow.at[1] * ph } as CSSProperties}
    />
  );
  return (
    <span className={[s.pet, down ? '' : pet.float === 'sway' ? s.petSway : s.petFly].join(' ')} style={style} aria-hidden="true">
      {glow}
      {wing?.style === 'spin' ? null : wings}
      <img className={s.petBody} src={assetUrl(pet.body)} alt="" draggable={false} />
      {wing?.style === 'spin' ? wings : null}
    </span>
  );
}

export function fighterName(manifest: AssetManifest | null, id: string): string {
  return manifest?.fighters.find((f) => f.id === id)?.name ?? id.charAt(0).toUpperCase() + id.slice(1);
}

export function bodyUrl(manifest: AssetManifest | null, id: string, state: BodyState): string {
  const f = manifest?.fighters.find((x) => x.id === id);
  return assetUrl(f?.images[state] ?? `generated/fighters/${id}/body_${state}.webp`);
}

export function fistUrl(manifest: AssetManifest | null, id: string): string {
  const f = manifest?.fighters.find((x) => x.id === id);
  return assetUrl(f?.fistImage ?? `generated/fighters/${id}/fist.webp`);
}

/**
 * Where the gloves go for a body drawn `h` px tall: rig units (S) to px. Centres are from the
 * feet (bottom centre): x toward the opponent, y upward.
 */
export function fistPlacement(rig: FighterRig, h: number) {
  const S = h / rig.body.drawHeight;
  const at = ([x, y]: [number, number]) => ({ x: x * S, y: -y * S });
  return { S, width: rig.fist.drawWidth * S, lead: at(rig.fists.rest.lead), rear: at(rig.fists.rest.rear) };
}

/** Inline styles that put a glove's centre on a point from the feet. */
export function fistStyle(width: number, at: { x: number; y: number }): CSSProperties {
  return { width, left: `calc(50% + ${at.x}px)`, bottom: at.y };
}

export interface FighterArtProps {
  id: string;
  state?: BodyState;
  /** Body height in px for an average-sized fighter. */
  height: number;
  /** Face left (the opponent side). */
  flip?: boolean;
  /** Both gloves at the guard pose (default), as in the fight. */
  fists?: boolean;
  /** bob: idle bob; win: victory jump; ko: tipped over; koDim: tipped over and dimmed (loser). */
  pose?: 'bob' | 'win' | 'ko' | 'koDim' | 'still';
  /** Seconds to offset the idle bob, so two fighters don't bob in sync. */
  delay?: number;
  shadow?: 'soft' | 'strong';
  className?: string;
  alt?: string;
  /** The Locker look (docs/game-design.md §11): the body's colour, the gloves, an effect and a pet. */
  look?: Look;
}

export function FighterArt({
  id,
  state = 'idle',
  height,
  flip = false,
  fists = true,
  pose = 'bob',
  delay = 0,
  shadow = 'strong',
  className,
  alt = '',
  look,
}: FighterArtProps) {
  const manifest = useManifest();
  const f = manifest?.fighters.find((x) => x.id === id);
  const ratio = f ? Math.min(1.2, Math.max(0.85, f.rig.body.drawHeight / REFERENCE_DRAW_HEIGHT)) : 1;
  const h = Math.round(height * ratio);
  const tint = f ? colorCss(look, f.color) : '';
  const effect = effectOf(look);
  const style = {
    '--h': `${h}px`,
    '--delay': `${delay}s`,
    ...(tint ? { '--look': tint } : {}),
    ...(effect ? { '--fx': effect.color } : {}),
  } as CSSProperties;
  const poseClass = pose === 'bob' ? s.bob : pose === 'win' ? s.win : pose === 'ko' ? s.ko : pose === 'koDim' ? s.koDim : '';
  const gloves = fists && f ? fistPlacement(f.rig, h) : null;
  const glove = gloveOf(manifest, look);
  const fistSrc = glove ? gloveUrl(glove) : fistUrl(manifest, id);
  const fistWidth = gloves && glove ? glove.drawWidth * gloves.S : (gloves?.width ?? 0);
  const pet = f && h >= PET_MIN_HEIGHT ? petOf(manifest, look) : null;
  // The pose moves the whole fighter, gloves too.
  return (
    <span className={[s.art, flip ? s.flip : '', className ?? ''].join(' ')} style={style}>
      <span className={[s.inner, poseClass].join(' ')}>
        {effect && (effect.style === 'aura' || effect.style === 'champion') && <span className={s.aura} aria-hidden="true" />}
        <img className={[s.body, shadow === 'soft' ? s.softShadow : ''].join(' ')} src={bodyUrl(manifest, id, state)} alt={alt} draggable={false} />
        {gloves && (
          <>
            <img className={[s.fist, s.rear].join(' ')} style={fistStyle(fistWidth, gloves.rear)} src={fistSrc} alt="" draggable={false} />
            <img className={[s.fist, s.lead].join(' ')} style={fistStyle(fistWidth, gloves.lead)} src={fistSrc} alt="" draggable={false} />
          </>
        )}
        {effect && (effect.style === 'sparks' || effect.style === 'champion') && (
          <span className={s.sparks} aria-hidden="true">
            {SPARKS.map((i) => (
              <span key={i} className={s.spark} style={{ '--i': i } as CSSProperties} />
            ))}
          </span>
        )}
      </span>
      {pet && f && <PetArt pet={pet} rig={f.rig} h={h} down={pose === 'ko' || pose === 'koDim'} />}
    </span>
  );
}

const SPARKS = [0, 1, 2, 3, 4, 5];
